import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  budget,
  isBeyondPlanningHorizon,
  localPeriod,
  maxPlanningPeriod,
  shiftMonth,
  type CategorySummary,
  type MonthSummary,
  type Transaction,
} from '../lib/budget';
import { centsToInput, formatCents, monthLabel, parseAmountToCents, shortDate } from '../lib/money';
import { AddExpense } from '../components/AddExpense';
import { MonthPicker } from '../components/MonthPicker';
import { useLiveReload } from '../lib/live-context';

function todayLocal(): { year: number; month: number } {
  return localPeriod();
}

function periodFromSearch(
  params: URLSearchParams,
  fallback: { year: number; month: number },
): { year: number; month: number } {
  const year = Number(params.get('y'));
  const month = Number(params.get('m'));
  if (!Number.isInteger(year) || year < 2000 || year > 2100) return fallback;
  if (!Number.isInteger(month) || month < 1 || month > 12) return fallback;
  if (isBeyondPlanningHorizon(year, month)) return maxPlanningPeriod();
  return { year, month };
}

export function Budget() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const fallback = todayLocal();
  const { year, month } = periodFromSearch(params, fallback);
  const fromHistory = Boolean(
    (location.state as { fromHistory?: boolean } | null)?.fromHistory,
  );

  const [summary, setSummary] = useState<MonthSummary | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editingLimits, setEditingLimits] = useState(false);

  const load = useCallback(async () => {
    const [s, t] = await Promise.all([
      budget.month(year, month),
      budget.transactions(year, month),
    ]);
    setSummary(s);
    setTransactions(t.transactions);
  }, [year, month]);

  useEffect(() => {
    setSummary(null);
    load().catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load.'));
  }, [load]);

  useLiveReload(() => {
    void load().catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load.'));
  });

  useEffect(() => {
    const y = params.get('y');
    const m = params.get('m');
    if (y === null && m === null) return;
    if (Number(y) === year && Number(m) === month) return;
    setParams({ y: String(year), m: String(month) }, { replace: true });
  }, [params, year, month, setParams]);

  function goTo(delta: number) {
    const next = shiftMonth(year, month, delta);
    if (delta > 0 && isBeyondPlanningHorizon(next.year, next.month)) return;
    setParams({ y: String(next.year), m: String(next.month) });
  }

  if (error) return <main className="page"><p className="error">{error}</p></main>;
  if (!summary) return <main className="page" aria-busy="true" />;

  const { totals } = summary;
  const spendable = summary.categories.filter((c) => !c.archived && !c.static);
  const isCurrent = year === fallback.year && month === fallback.month;
  const nextMonth = shiftMonth(year, month, 1);
  const canGoNext = !isBeyondPlanningHorizon(nextMonth.year, nextMonth.month);

  return (
    <main className="page">
      {(fromHistory || !isCurrent) && (
        <Link className="back-link" to="/history">
          ← History
        </Link>
      )}
      <MonthPicker
        year={year}
        month={month}
        onPrev={() => goTo(-1)}
        onNext={canGoNext ? () => goTo(1) : undefined}
        onToday={isCurrent ? undefined : () => setParams({})}
      />

      <section className="totals">
        <div>
          <span className="meta">Budgeted</span>
          <strong>{formatCents(totals.limitCents)}</strong>
        </div>
        <div>
          <span className="meta">Spent</span>
          <strong>{formatCents(totals.spentCents)}</strong>
        </div>
        <div>
          <span className="meta">Left</span>
          <strong className={totals.remainingCents < 0 ? 'over' : 'under'}>
            {formatCents(totals.remainingCents)}
          </strong>
        </div>
      </section>

      {summary.categories.length === 0 ? (
        <p className="hint">
          No categories yet. Add some on the Categories page, then set limits here.
        </p>
      ) : editingLimits ? (
        <LimitEditor
          year={year}
          month={month}
          categories={summary.categories}
          onDone={async () => {
            setEditingLimits(false);
            await load();
          }}
        />
      ) : (
        <>
          <ul className="list categories">
            {summary.categories.map((c) => (
              <CategoryRow key={c.id} category={c} />
            ))}
          </ul>
          <div className="row">
            <button className="secondary" onClick={() => setEditingLimits(true)}>
              Edit limits
            </button>
            {spendable.length > 0 && (
              <button className="primary" onClick={() => setAdding(true)}>
                Add expense
              </button>
            )}
          </div>
        </>
      )}

      <section className="panel">
        <h2>Expenses</h2>
        {transactions.length === 0 ? (
          <p className="hint">Nothing recorded for {monthLabel(year, month)}.</p>
        ) : (
          <ul className="list">
            {transactions.map((t) => (
              <TransactionRow key={t.id} tx={t} onChanged={load} />
            ))}
          </ul>
        )}
      </section>

      <Link className="link block-link" to="/history">
        View all months
      </Link>

      {adding && (
        <AddExpense
          year={year}
          month={month}
          categories={summary.categories.filter((c) => !c.archived && !c.static)}
          onClose={() => setAdding(false)}
          onSaved={async () => {
            setAdding(false);
            await load();
          }}
        />
      )}
    </main>
  );
}

function CategoryRow({ category }: { category: CategorySummary }) {
  // Cap the bar at 100% so an overspent category doesn't render off the row;
  // the colour and the negative number carry the "over" signal instead.
  const pct =
    category.limitCents > 0
      ? Math.min(100, Math.round((category.spentCents / category.limitCents) * 100))
      : category.spentCents > 0
        ? 100
        : 0;

  return (
    <li className="category">
      <div className="category-head">
        <strong>
          {category.name}
          {category.static && <span className="badge">bill</span>}
          {category.archived && <span className="badge muted">archived</span>}
        </strong>
        <span className={category.overBudget ? 'over' : category.static ? 'muted-text' : 'under'}>
          {category.static ? 'auto-paid' : `${formatCents(category.remainingCents)} left`}
        </span>
      </div>
      <div className="bar" role="presentation">
        <div
          className={category.overBudget ? 'fill over-fill' : 'fill'}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="meta">
        {formatCents(category.spentCents)} of {formatCents(category.limitCents)}
      </span>
    </li>
  );
}

function TransactionRow({ tx, onChanged }: { tx: Transaction; onChanged: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);

  async function remove() {
    if (!window.confirm(`Delete ${formatCents(tx.amountCents)} from ${tx.categoryName}?`)) return;
    setBusy(true);
    try {
      await budget.deleteTransaction(tx.id);
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <li>
      <div>
        <strong>
          {formatCents(tx.amountCents)} · {tx.categoryName}
        </strong>
        <span className="meta">
          {shortDate(tx.date)} · {tx.createdBy.displayName}
          {tx.note ? ` · ${tx.note}` : ''}
        </span>
      </div>
      <button className="danger" onClick={() => void remove()} disabled={busy}>
        Delete
      </button>
    </li>
  );
}

function LimitEditor({
  year,
  month,
  categories,
  onDone,
}: {
  year: number;
  month: number;
  categories: CategorySummary[];
  onDone: () => Promise<void>;
}) {
  const [values, setValues] = useState<Record<number, string>>(() =>
    Object.fromEntries(categories.map((c) => [c.id, centsToInput(c.limitCents)])),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    const limits: { categoryId: number; limitCents: number }[] = [];
    for (const c of categories) {
      const raw = values[c.id] ?? '0';
      const cents = raw.trim() === '' ? 0 : parseAmountToCents(raw);
      if (cents === null || cents < 0) {
        setError(`"${raw}" is not a valid amount for ${c.name}.`);
        return;
      }
      limits.push({ categoryId: c.id, limitCents: cents });
    }

    setBusy(true);
    setError(null);
    try {
      await budget.setLimits(year, month, limits);
      await onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save limits.');
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>Limits for {monthLabel(year, month)}</h2>
      <p className="hint">Applies to this month only. Next month copies these forward.</p>
      {categories.map((c) => (
        <label key={c.id}>
          {c.static ? `${c.name} (bill)` : c.name}
          <input
            value={values[c.id] ?? ''}
            onChange={(e) => setValues((v) => ({ ...v, [c.id]: e.target.value }))}
            inputMode="decimal"
            placeholder="0.00"
          />
        </label>
      ))}
      {error && <p className="error">{error}</p>}
      <div className="row">
        <button className="secondary" onClick={() => void onDone()} disabled={busy}>
          Cancel
        </button>
        <button className="primary" onClick={() => void save()} disabled={busy}>
          {busy ? 'Saving…' : 'Save limits'}
        </button>
      </div>
    </section>
  );
}
